/**
 * 회원 상세 모달 > 스케줄 탭의 월별 캘린더 뷰.
 *
 * - 학생이 작성한 월간 계획(monthly_schedule_plans + entries)을 캘린더로 표시
 * - 월 이동 가능 (이전/다음)
 * - 각 날짜에 등록된 교시 표시
 * - 해당 날짜에 승인된 사유서가 있으면 결석/지각/조퇴 표시
 * - 미작성 plan / pending / approved 상태 배지
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import MonthlyScheduleCalendar, { CalendarEntry, CalendarException } from './MonthlyScheduleCalendar';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getToken = () =>
  localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');

interface Plan {
  id: string;
  year_month: string;
  status: string;
  entries?: CalendarEntry[];
}

interface Props {
  studentId: string;
}

const STATUS_LABELS: Record<string, string> = {
  draft: '작성 중',
  pending: '학부모 승인 대기',
  parent_approved: '학부모 승인 / 독서실 확인 대기',
  approved: '승인 완료',
  rejected: '거절됨',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  parent_approved: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const MemberMonthlyScheduleView: React.FC<Props> = ({ studentId }) => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [plan, setPlan] = useState<Plan | null>(null);
  const [exceptions, setExceptions] = useState<CalendarException[]>([]);
  const [loading, setLoading] = useState(true);

  const ymKey = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = String(currentDate.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [currentDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/student/${studentId}?year_month=${ymKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const list = data?.data || [];
        setPlan(list[0] || null);
      } else {
        setPlan(null);
      }
    } catch {
      setPlan(null);
    }
    // 사유서 (조직 단위) — 학생 본인 거 필터
    try {
      const token = getToken();
      const r = await fetch(
        `${API_BASE_URL}/v1/owner/schedule-exceptions/requests?status=all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const data = await r.json();
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setExceptions(arr.filter((e: any) => e.student_id === studentId));
      }
    } catch {
      // ignore
    }
    setLoading(false);
  }, [studentId, ymKey]);

  useEffect(() => {
    load();
  }, [load]);

  const goPrev = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const goNext = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-3 text-sm text-gray-600">스케줄 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 헤더 — 월 이동 + plan 상태 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={goPrev}
            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
            type="button"
          >
            ◀
          </button>
          <h3 className="text-lg font-bold text-gray-800">
            {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
          </h3>
          <button
            onClick={goNext}
            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
            type="button"
          >
            ▶
          </button>
        </div>
        <div>
          {plan ? (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[plan.status] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABELS[plan.status] || plan.status}
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
              월간 계획 미작성
            </span>
          )}
        </div>
      </div>

      <MonthlyScheduleCalendar
        year={currentDate.getFullYear()}
        month={currentDate.getMonth() + 1}
        entries={plan?.entries || []}
        exceptions={exceptions}
      />

      {/* 가이드 */}
      {!plan && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          이 달은 따로 변경한 일정이 없어 <b>등록된 스케줄이 그대로 적용</b>됩니다. 출결도 이 기준으로 판정됩니다.
        </div>
      )}
    </div>
  );
};

export default MemberMonthlyScheduleView;
